import { useToast } from '@/hooks/use-toast';
import { Toast, ToastClose, ToastProvider, ToastViewport } from '@/components/ui/toast';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

// Default toast duration in milliseconds
const TOAST_DURATION = 5000;

// Progress bar component with countdown animation
function ToastProgress({ variant, duration = TOAST_DURATION }: { variant?: string; duration?: number }) {
  const getProgressColor = () => {
    switch (variant) {
      case 'success':
        return 'bg-emerald-500';
      case 'destructive':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200/30 overflow-hidden rounded-b-md">
      <div
        className={`h-full ${getProgressColor()} origin-left`}
        style={{
          animation: `toast-progress ${duration}ms linear forwards`,
        }}
      />
      <style>{`
        @keyframes toast-progress {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
      `}</style>
    </div>
  );
}

export function Toaster() {
  const { toasts } = useToast();

  const getIcon = (variant: string) => {
    switch (variant) {
      case 'success':
        return (
          <CheckCircle
            className="me-3 -mt-0.5 inline-flex text-emerald-500"
            size={16}
            aria-hidden="true"
          />
        );
      case 'destructive':
        return (
          <XCircle className="me-3 -mt-0.5 inline-flex text-red-500" size={16} aria-hidden="true" />
        );
      case 'warning':
        return (
          <AlertCircle
            className="me-3 -mt-0.5 inline-flex text-yellow-500"
            size={16}
            aria-hidden="true"
          />
        );
      case 'info':
        return (
          <Info className="me-3 -mt-0.5 inline-flex text-blue-500" size={16} aria-hidden="true" />
        );
      default:
        return null;
    }
  };

  return (
    <ToastProvider duration={TOAST_DURATION}>
      {toasts.map(function ({ id, title, description, action, variant, duration, ...props }) {
        return (
          <Toast key={id} variant={variant} duration={duration} {...props}>
            <div className="flex items-center w-full">
              {getIcon(variant || 'default')}
              <div className="flex-1">
                <p className="text-sm">
                  {title && <span className="font-medium">{title}</span>}
                  {description && (
                    <span className="block text-muted-foreground">{description}</span>
                  )}
                </p>
              </div>
              {action}
              <ToastClose />
            </div>
            <ToastProgress variant={variant ?? undefined} duration={duration || TOAST_DURATION} />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
